import { GraphQLError } from "graphql"
import { v4 as uuid } from "uuid"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { findCustomerTargetAddress, formCustomerCustomFieldsAction } from "../utils/customerAddressInformation"
import { addressValidation } from "../validation/addressValidation"

export const mutationCreateCustomerAddress = {
  async createCustomerAddress(parent, args, context, info) {
    const { customerId, customerKey, customerNumber, addressType, values } = args
    const commerceApi = CommerceService.api()
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ADDRESSES,
      operation: Operations.CREATE,
    })

    const customer = await getCustomer({ id: customerId, key: customerKey, number: customerNumber })

    await internalCustomerException({ id: customer.id })
    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    // validate inputs
    try {
      await addressValidation().validate(values)
    } catch (err) {
      console.warn("Customer address validation error", err)
      return null
    }

    const addressKey = `address_${customer.customerNumber}_${uuid()}`

    let addressAction = null

    switch (addressType) {
      case "SHIPPING":
        addressAction = "addShippingAddressId"
        break
      case "BILLING":
        addressAction = "addBillingAddressId"
        break
      default:
        addressAction = null
    }

    isExistingAddress(customer.addresses, customer?.shippingAddressIds, addressType, values)

    delete values.addressType

    const addressObject = {
      ...values,
      key: addressKey,
      ...(values?.country && { country: values.country.toUpperCase() }),
    }

    const addressCreatePayload = {
      version: customer.version,
      actions: [
        {
          action: "addAddress",
          address: addressObject,
        },
      ],
    }

    try {
      const customerWithNewAddress = (
        await commerceApi
          .customers()
          .withId({ ID: customer.id })
          .post({
            body: JSON.stringify(addressCreatePayload),
          })
          .execute()
      )?.body

      // request returns updated customer but not an address it self
      // need to find it manually using the key
      const newAddress = customerWithNewAddress.addresses.find((address) => address.key === addressKey)

      if (!newAddress) return null

      let actions = []

      if (addressAction) {
        actions.push({
          action: addressAction,
          addressId: newAddress.id,
        })
      }

      let targetStatus = ""
      if (values?.customFields?.status) {
        switch (values?.customFields?.status) {
          case "DELETED":
            targetStatus = "X"
            break
          case "CLOSED":
            targetStatus = "S"
            break
          default:
            targetStatus = "A"
        }
      }

      // can't set this together with addAddress
      actions.push(
        await formCustomerCustomFieldsAction(newAddress, targetStatus, {
          creationAt: new Date(),
          creationBy: user.employeeNumber,
          modifiedBy: user.employeeNumber,
          versionNr: 0,
          benefitRecipient: values?.customFields?.benefitRecipient,
          dateOfBirth: values?.customFields?.dateOfBirth,
        }),
      )

      const addressActionPayload = {
        version: customerWithNewAddress.version,
        actions,
      }

      const customerWithUpdatedAddress = (
        await commerceApi
          .customers()
          .withId({ ID: customer.id })
          .post({
            body: JSON.stringify(addressActionPayload),
          })
          .execute()
      )?.body

      const updatedNewAddress = findCustomerTargetAddress(customerWithUpdatedAddress, newAddress.id)

      return updatedNewAddress
    } catch (err) {
      throw new Error(err)
    }
  },
}

const isExistingAddress = (addresses, shippingAddressIds, addressType, values) => {
  if (addressType !== "SHIPPING" || shippingAddressIds.length === 0) {
    return
  }

  const shippingAddresses = addresses.filter((address) => shippingAddressIds.includes(address.id))

  if (shippingAddresses.length === 0) {
    return
  }

  const adjustedValues = { ...values }
  delete adjustedValues.customFields

  const mappedShippingAddresses = shippingAddresses.map((address) => {
    delete address.custom
    delete address.externalId
    delete address.id
    delete address.key

    return {
      ...address,
    }
  })

  const exists = mappedShippingAddresses.some((address) =>
    Object.keys(adjustedValues).every((key) => address[key]?.trim() === adjustedValues[key]?.trim()),
  )

  if (!exists) {
    return
  }

  throw new GraphQLError("ADDRESS_EXISTS", {
    extensions: {
      code: "ADDRESS_EXISTS",
    },
  })
}
